import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Archive as ArchiveIcon, DownloadSimple, Headset, LockSimple, CaretLeft, CaretRight } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/context/AuthContext';
import { useLineups } from './daily/useLineups';
import { StatsBar } from './daily/StatsBar';
import { FiltersBar } from './daily/FiltersBar';
import { CompanyCards } from './daily/CompanyCards';
import { LineupTable } from './daily/LineupTable';
import { LineupByReport } from './daily/LineupByReport';
import { fmtDate, downloadBlob, aggregateCompanies, aggregateReport } from './daily/helpers';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const btn = 'border-2 border-[#0A0A0A] rounded-md font-bold px-4 py-2 shadow-[2px_2px_0px_0px_rgba(10,10,10,1)] hover:-translate-y-0.5 hover:shadow-[4px_4px_0px_0px_rgba(10,10,10,1)] transition-all active:translate-y-0.5 active:shadow-none';

const shiftDay = (iso, delta) => {
  const d = new Date(`${iso}T00:00:00`);
  d.setDate(d.getDate() + delta);
  return d.toISOString().slice(0, 10);
};

const Archive = () => {
  const { isOwner } = useAuth();
  const navigate = useNavigate();
  const [dates, setDates] = useState([]);
  const [today, setToday] = useState('');
  const [date, setDate] = useState('');
  const [mode, setMode] = useState('day'); // 'day' | 'range'
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');

  useEffect(() => {
    axios.get(`${API}/lineups/dates`).then(({ data }) => {
      setToday(data.today);
      setDates(data.dates.filter((d) => d < data.today));
      const y = shiftDay(data.today, -1);
      setDate(data.dates.find((d) => d < data.today) || y);
      setFrom(shiftDay(data.today, -7));
      setTo(y);
    }).catch(() => toast.error('Failed to load archive dates'));
  }, []);

  const query = useMemo(() => (mode === 'range' ? { date_from: from, date_to: to } : date ? { date } : null), [mode, from, to, date]);
  const L = useLineups(query);
  const isArchived = useMemo(() => (mode === 'range' ? !!to && !!today && to < today : !!date && !!today && date < today), [mode, to, date, today]);
  const rangeLabel = mode === 'range' ? `${fmtDate(from)} → ${fmtDate(to)}` : fmtDate(date);

  const exportDay = async () => {
    try {
      const params = { ...(mode === 'range' ? { date_from: from, date_to: to } : { date }), ...(L.company ? { company: L.company } : {}), ...(L.lineupBy ? { lineup_by: L.lineupBy } : {}), ...(L.telecaller ? { telecaller: L.telecaller } : {}) };
      const { data } = await axios.get(`${API}/lineups/export.xlsx`, { params, responseType: 'blob' });
      downloadBlob(data, `TulsiJobs_Lineups_${mode === 'range' ? `${from}_to_${to}` : date}.xlsx`);
      toast.success('Archive exported');
    } catch { toast.error('Export failed'); }
  };

  if (!date || !from || L.loading) return <div className="flex items-center justify-center h-full"><div className="text-xl font-bold text-white">Loading...</div></div>;

  return (
    <div data-testid="archive-page">
      <div className="mb-6 flex flex-wrap items-end gap-4">
        <div>
          <h1 className="text-4xl sm:text-5xl font-black tracking-tighter text-white mb-1 flex items-center gap-3" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
            <ArchiveIcon size={44} weight="duotone" className="text-[#F9A8D4]" /> Daily Archive
          </h1>
          <p className="text-base font-medium text-white flex items-center gap-2">
            <LockSimple size={16} weight="fill" className="text-[#F9A8D4]" /> Past days are view-only. Pick a date to see who lined up whom and the final status.
          </p>
        </div>
        <div className="ml-auto flex flex-wrap gap-2">
          <Button data-testid="open-daily-btn" onClick={() => navigate('/daily')} className={`${btn} bg-[#A3E635] text-black`}>
            <Headset size={20} weight="bold" className="mr-2" /> Today's Board
          </Button>
          <Button data-testid="export-archive-btn" onClick={exportDay} className={`${btn} bg-[#93C5FD] text-black`}>
            <DownloadSimple size={20} weight="bold" className="mr-2" /> Export Excel
          </Button>
        </div>
      </div>

      <div className="mb-4 flex flex-wrap items-center gap-3 bg-[#0F3622] border-2 border-[#F9A8D4] rounded-md p-3" data-testid="archive-date-bar">
        <div className="flex rounded-md overflow-hidden border-2 border-black" data-testid="archive-mode-toggle">
          <button data-testid="archive-mode-day" onClick={() => setMode('day')} className={`px-3 py-1.5 text-xs font-black uppercase ${mode === 'day' ? 'bg-[#F9A8D4] text-black' : 'bg-white/10 text-white hover:bg-white/20'}`}>Single day</button>
          <button data-testid="archive-mode-range" onClick={() => setMode('range')} className={`px-3 py-1.5 text-xs font-black uppercase ${mode === 'range' ? 'bg-[#F9A8D4] text-black' : 'bg-white/10 text-white hover:bg-white/20'}`}>Date range</button>
        </div>
        {mode === 'day' ? (
          <>
            <button data-testid="archive-prev-day" onClick={() => setDate(shiftDay(date, -1))} className="p-2 bg-white text-black border-2 border-black rounded-md hover:-translate-y-0.5 transition-all"><CaretLeft size={18} weight="bold" /></button>
            <div className="bg-[#F9A8D4] text-black border-2 border-black rounded-md px-4 py-2 font-black text-sm uppercase tracking-wider min-w-[150px] text-center" data-testid="archive-date-label">{fmtDate(date)}</div>
            <button data-testid="archive-next-day" disabled={shiftDay(date, 1) >= today} onClick={() => setDate(shiftDay(date, 1))} className="p-2 bg-white text-black border-2 border-black rounded-md hover:-translate-y-0.5 transition-all disabled:opacity-40"><CaretRight size={18} weight="bold" /></button>
            <input data-testid="archive-date-input" type="date" value={date} max={shiftDay(today, -1)} onChange={(e) => e.target.value && setDate(e.target.value)} className="bg-white border-2 border-black rounded-md px-3 py-1.5 text-black font-bold text-sm" />
            {dates.length > 0 && (
              <select data-testid="archive-date-quick" value={dates.includes(date) ? date : ''} onChange={(e) => e.target.value && setDate(e.target.value)} className="bg-white border-2 border-black rounded-md px-2 py-1.5 text-black font-bold text-sm">
                <option value="">Days with data…</option>
                {dates.map((d) => <option key={d} value={d}>{fmtDate(d)}</option>)}
              </select>
            )}
            {!isArchived && <span className="px-2 py-1 bg-[#FDE047] text-black border-2 border-black rounded text-[11px] font-black" data-testid="archive-live-warning">This is today — still live on the Daily board</span>}
          </>
        ) : (
          <>
            <label className="text-[10px] font-bold uppercase tracking-wider text-white">From</label>
            <input data-testid="archive-from" type="date" value={from} max={to || undefined} onChange={(e) => e.target.value && setFrom(e.target.value)} className="bg-white border-2 border-black rounded-md px-3 py-1.5 text-black font-bold text-sm" />
            <label className="text-[10px] font-bold uppercase tracking-wider text-white">To</label>
            <input data-testid="archive-to" type="date" value={to} min={from || undefined} onChange={(e) => e.target.value && setTo(e.target.value)} className="bg-white border-2 border-black rounded-md px-3 py-1.5 text-black font-bold text-sm" />
            <div className="bg-[#F9A8D4] text-black border-2 border-black rounded-md px-3 py-1.5 font-black text-xs uppercase tracking-wider" data-testid="archive-range-label">{rangeLabel}</div>
            <div className="flex gap-1">
              {[7, 15, 30].map((n) => (
                <button key={n} data-testid={`archive-range-last-${n}`} onClick={() => { setFrom(shiftDay(today, -n)); setTo(shiftDay(today, -1)); }} className="px-2 py-1 bg-white text-black border-2 border-black rounded text-[10px] font-bold hover:bg-[#F9A8D4]">Last {n} days</button>
              ))}
            </div>
          </>
        )}
      </div>

      <StatsBar summary={L.data?.summary} />

      <FiltersBar
        showDate={false}
        company={L.company} setCompany={L.setCompany} companies={L.options.companies}
        lineupBy={L.lineupBy} setLineupBy={L.setLineupBy} lineupBys={L.options.lineupBys}
        telecaller={L.telecaller} setTelecaller={L.setTelecaller} telecallers={L.options.telecallers}
        onClear={L.clearFilters} count={L.rows.length} total={L.data?.rows?.length || 0}
      />

      <section className="mb-6">
        <CompanyCards companies={aggregateCompanies(L.rowsNoCompany)} selected={L.company} onSelect={L.setCompany} />
      </section>

      <section className="mb-6">
        <LineupTable
          rows={L.rows} company={L.company} onClearCompany={() => L.setCompany('')}
          readOnly={isArchived} today={today} showDate={mode === 'range'} isOwner={isOwner}
          onStatusChange={(row, status) => { if (status !== 'Not Going') L.updateStatus(row, status); else toast.info('Use the Daily board to mark Not Going'); }}
          onDelete={L.remove} onSendToTracker={L.sendToTracker}
        />
      </section>

      <section className="mb-6">
        <LineupByReport report={aggregateReport(L.rows)} title={`Lineup By Performance · ${rangeLabel}`} />
      </section>
    </div>
  );
};

export default Archive;
